import { useEffect } from "react";
import { brandedTitle } from "../lib/seoTitle";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { EditorialPage } from "../components/editorial";
import MeshField from "../components/story/MeshField";
import Doodle from "../components/story/Doodle";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { renderMarkdown } from "../lib/markdown";
import { getPost, articleJsonLd, formatDate } from "../lib/blog";

/**
 * /blog/:slug — one LetsLoop blog post (LL-0095). Renders the post body with the
 * shared markdown renderer and emits its schema.org Article JSON-LD inline (so it
 * is present in the prerendered SSR HTML for SEO/GEO, not just after hydration).
 * scripts/prerender.mjs patches <title>/<meta>/<canonical> in the static <head>.
 */
export default function BlogPostPage() {
  const { slug = "" } = useParams<{ slug: string }>();
  const post = getPost(slug);

  useEffect(() => {
    if (!post) {
      document.title = "Post not found | LetsLoop";
      return;
    }
    document.title = brandedTitle(post.title);
    const desc = document.querySelector('meta[name="description"]');
    if (desc && post.description)
      desc.setAttribute("content", post.description);
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute("href", post.canonical);
  }, [post]);

  if (!post) {
    return (
      <>
        <Navbar />
        <EditorialPage className="flex items-center justify-center px-4 py-32">
          <div className="relative max-w-xl w-full text-center">
            <h1
              className="font-headline text-4xl md:text-5xl tracking-[-0.025em] text-ink mb-5"
              style={{ fontWeight: 700 }}
            >
              We could not find that post.
            </h1>
            <p className="text-gray-600 leading-relaxed mb-8">
              It may have moved. Browse the latest writing instead.
            </p>
            <Link
              to="/news"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-salmon"
            >
              All posts
              <ArrowRight size={16} />
            </Link>
          </div>
        </EditorialPage>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <EditorialPage>
        {/* Mesh band behind the top of the post only. A post is long-form
            reading, so the brand surface introduces it and then gets out of the
            way rather than sitting under the body text. Fixed height, absolutely
            placed, aria-hidden. */}
        <div
          aria-hidden="true"
          className="absolute inset-x-0 top-0 h-[420px] overflow-hidden pointer-events-none"
        >
          <MeshField variant="ambient" />
          <Doodle
            name="starFilled"
            size={22}
            className="absolute w-4 h-4 md:w-auto md:h-auto left-[6%] top-[42%] text-salmon/55"
          />
          <Doodle
            name="squiggle"
            size={70}
            rotate={-6}
            className="absolute w-7 h-7 md:w-auto md:h-auto right-[6%] top-[34%] text-teal/30"
          />
        </div>

        <article className="relative max-w-3xl mx-auto px-4 md:px-6 pt-32 md:pt-40 pb-16 md:pb-24">
          {/* JSON-LD emitted inline so it lands in the prerendered HTML (GEO/SEO). */}
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: articleJsonLd(post) }}
          />

          <Link
            to="/news"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-ink/60 hover:text-salmon transition-colors mb-10"
          >
            <ArrowLeft size={16} />
            All posts
          </Link>

          <header className="mb-10 pb-8 border-b border-ink/15">
            {post.date && (
              <p className="text-xs font-bold text-salmon uppercase tracking-widest mb-3">
                {formatDate(post.date)} &middot; {post.readingMinutes} min read
              </p>
            )}
            <h1 className="font-serif text-4xl md:text-6xl tracking-tight leading-[0.97] text-ink">
              {post.title}
            </h1>
            {post.description && (
              <p className="text-gray-600 leading-relaxed mt-5 text-lg md:text-xl">
                {post.description}
              </p>
            )}
          </header>

          {/* Optional hero image. Posts without an `image` field publish
              text-only exactly as before, so this is additive. */}
          {post.image && (
            <img
              src={post.image}
              alt={post.title}
              className="w-full rounded-2xl object-cover mb-10 border border-ink/10"
              loading="eager"
            />
          )}

          {renderMarkdown(post.body)}

          <footer className="mt-16 pt-8 border-t border-ink/15">
            <p className="font-serif text-2xl md:text-3xl tracking-tight text-ink mb-4">
              Ready to find your next loop?
            </p>
            <Link
              to="/download"
              className="inline-flex items-center gap-1.5 text-base font-semibold text-salmon border-b-2 border-salmon/30 hover:border-salmon transition-colors"
            >
              See what is on, in the app
              <ArrowRight size={18} />
            </Link>
          </footer>
        </article>
      </EditorialPage>
      <Footer />
    </>
  );
}
